/**
 * Hooks TanStack Query — matrice d'éligibilité (S5.5).
 *
 * Pattern aligné sur `useCatalog.ts` :
 *   - query keys hiérarchiques (`eligibilityKeys`)
 *   - mutation PATCH bulk invalide rules + blocking-tags + tous les
 *     work-previews (les caractéristiques d'éligibilité changent
 *     potentiellement pour toutes les œuvres).
 *
 * `useWorkEligibility` utilise `staleTime: 30s` car le banner
 * d'éligibilité est consulté plusieurs fois lors du tri /drafts —
 * inutile de re-fetcher à chaque mount du composant.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { draftsKeys } from '@/hooks/useDrafts'
import type {
  BlockingTagResponse,
  EligibilityBulkUpdateRequest,
  EligibilityBulkUpdateResponse,
  EligibilityRuleResponse,
  WorkEligibilityPreview,
} from '@/types/eligibility'
import { eligibilityApi } from '@/utils/api/eligibility'

// ──────────────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────────────

export const eligibilityKeys = {
  all: ['eligibility'] as const,
  rules: () => [...eligibilityKeys.all, 'rules'] as const,
  blockingTags: () => [...eligibilityKeys.all, 'blocking-tags'] as const,
  workPreview: (workId: number) => [...eligibilityKeys.all, 'works', workId] as const,
}

// ──────────────────────────────────────────────────────────────────────────────
// Lecture matrice + tags bloquants
// ──────────────────────────────────────────────────────────────────────────────

export function useEligibilityRules() {
  return useQuery<EligibilityRuleResponse[]>({
    queryKey: eligibilityKeys.rules(),
    queryFn: eligibilityApi.listRules,
    staleTime: 5 * 60_000, // 5 min — la matrice ne change qu'au PATCH bulk
  })
}

export function useBlockingTags() {
  return useQuery<BlockingTagResponse[]>({
    queryKey: eligibilityKeys.blockingTags(),
    queryFn: eligibilityApi.listBlockingTags,
    staleTime: 5 * 60_000,
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// PATCH bulk
// ──────────────────────────────────────────────────────────────────────────────

export function useEligibilityBulkUpdate() {
  const qc = useQueryClient()
  return useMutation<EligibilityBulkUpdateResponse, Error, EligibilityBulkUpdateRequest>({
    mutationFn: eligibilityApi.patchBulk,
    onSuccess: (response) => {
      // Pose les caches rules + blocking_tags directement (pas de re-fetch
      // car la réponse est déjà la matrice rechargée).
      qc.setQueryData(eligibilityKeys.rules(), response.rules)
      qc.setQueryData(eligibilityKeys.blockingTags(), response.blocking_tags)
      // Invalide tous les work-previews — les caractéristiques d'éligibilité
      // par œuvre sont potentiellement impactées par le changement de matrice.
      qc.invalidateQueries({ queryKey: [...eligibilityKeys.all, 'works'] })
      // Invalide la liste de drafts (`/drafts`) — pas changée techniquement
      // mais la feature D ("X drafts manquants") va recompter selon la
      // nouvelle matrice.
      qc.invalidateQueries({ queryKey: draftsKeys.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Preview par œuvre — alimenta `<EligibilityPreviewBanner>` côté DraftsTab
// ──────────────────────────────────────────────────────────────────────────────

export function useWorkEligibility(workId: number | null | undefined) {
  return useQuery<WorkEligibilityPreview>({
    queryKey: eligibilityKeys.workPreview(workId ?? 0),
    queryFn: () => eligibilityApi.getWorkEligibility(workId!),
    enabled: workId != null && workId > 0,
    // Banner consulté plusieurs fois pendant le tri ; 30s évite la redondance
    // mais reste réactif si la matrice vient de changer.
    staleTime: 30_000,
  })
}
